import React from 'react';
import { Mui } from '@osu/react-ui';
import { useAdminContext } from '../../context/AdminContext';
import useRoleAuthentication from '../../../Authentication/hooks/useRoleAuthentication';
import useStudentSearch from '../../hooks/useStudentSearch';
import SearchInput from './SearchInput';
import SearchStatus from './SearchStatus';
import SelectedStudentCard from './SelectedStudentCard';
import SearchResults from './SearchResults';

/**
 * StudentSearchInterface Component (Refactored)
 * 
 * Main container component that orchestrates the student search functionality.
 * Now much simpler and focused on composition rather than implementation.
 * 
 * @param {Object} props - Component props
 * @param {Function} props.onStudentSelect - Callback when student is selected
 * @param {Object|null} props.selectedStudent - Currently selected student
 * @param {boolean} props.isLoading - Whether search is in progress
 * @param {string} props.searchQuery - Current search query (controlled by parent)
 * @param {Array} props.searchResults - Current search results (controlled by parent)
 * @param {boolean} props.hasSearched - Whether a search has been performed (controlled by parent)
 * @param {Function} props.onSearchStateChange - Callback to update parent search state
 */
function StudentSearchInterface(props) {
  // Context and permissions
  const { isAnyAdmin } = useAdminContext();
  const { canSearchStudents } = useRoleAuthentication();

  // Extract all search logic into custom hook
  const searchState = useStudentSearch(props);

  // Don't render if user doesn't have search permissions
  if (!isAnyAdmin || !canSearchStudents) {
    return (
      <Mui.Alert severity="warning">
        You do not have permission to search for students.
      </Mui.Alert>
    );
  }

  return (
    <Mui.Box sx={{ width: '100%' }}>
      {/* Search Input Area - Constrained width for better UX */}
      <Mui.Box sx={{ maxWidth: 600, mb: 3 }}>
        {/* Search Header */}
        <Mui.Typography variant="h6" gutterBottom>
          Student Search
        </Mui.Typography>
        
        {/* Search Input */}
        <SearchInput {...searchState.inputProps} />
        
        {/* Status Messages (Loading/Error) */}
        <SearchStatus {...searchState.statusProps} />
        
        {/* Selected Student Display */}
        <SelectedStudentCard {...searchState.selectedProps} />
      </Mui.Box>
      
      {/* Search Results - Full width for better visibility */}
      <SearchResults {...searchState.resultsProps} />
    </Mui.Box>
  );
}

export default StudentSearchInterface;